import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {faFilter, faTimesCircle} from '@fortawesome/pro-solid-svg-icons';
import {tagToString} from '@webmate-web-core/shared/types/tag';
import {
    ActivityExecutionStatus,
    AssessmentEvaluationStatus,
    AssessmentTask,
    AuditActivitiesFilterSpec,
    HydratedAuditTarget
} from '@webmate-web-core/shared/services/audit.service';
import {SelectItem} from 'primeng/api';
import {BrowserSessionStateArtifact} from '@webmate-web-core/shared/types/browsersession';

import {FormsModule} from '@angular/forms';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {TagsModule} from '@webmate-web-core/shared/components/tags/tags.module';
import {InputTextModule} from 'primeng/inputtext';
import {Spec, StateObservationTarget} from '@webmate-web-core/shared/types/testmgmt';
import {IdBasedSpecReference} from '@webmate-web-core/shared/types/ids';
import {AuditRequirement} from '@webmate-web-core/shared/tools/audit/audittemplate/shared';
import {MultiSelect} from 'primeng/multiselect';
import {Checkbox} from 'primeng/checkbox';
import {NgClass} from '@angular/common';

export type FilterType = 'spec' | 'testObject' | 'text' | 'tag' |
    'evaluationStatus' | 'executionStatus' | 'results';

@Component({
    selector: 'wm-activities-filter',
    templateUrl: './activity-filter.component.html',
    styleUrls: ['./activity-filter.component.less'],
    // changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, FontAwesomeModule, TagsModule, InputTextModule, MultiSelect, Checkbox, NgClass]
})
export class ActivityFilterComponent implements OnChanges {

    @Input() testObjects: HydratedAuditTarget[] = [];
    @Input() states: {[id: string]: BrowserSessionStateArtifact};
    @Input() requirements: AuditRequirement[];
    @Input() specs: Spec[];
    @Input() tasks: AssessmentTask[];
    @Input() shownFilters?: FilterType[];
    @Input() hideFiltersDisplay?: boolean = false;

    @Output() filterChange = new EventEmitter<AuditActivitiesFilterSpec>();

    shownFilterSet?: Set<FilterType>;

    showWithResults: boolean = false;

    filtersActive: boolean = false;
    showFilters: boolean = false;

    filter: AuditActivitiesFilterSpec;
    filterText: string = '';

    availableEvaluationStatuses: SelectItem[] = [];
    selectedEvaluationStatuses: AssessmentEvaluationStatus[] = [];

    availableExecutionStatuses: SelectItem[] = [];
    selectedExecutionStatuses: ActivityExecutionStatus[] = [];

    availableTestObjects: SelectItem[] = [];
    selectedTestObjects: string[] = [];

    availableSpecGroups: SelectItem[] = [];
    selectedSpecGroups: IdBasedSpecReference[] = [];

    availableTags: SelectItem[] = [];
    selectedTags: string[] = [];

    statesByTestObjectId: { [testObjectId: string]: BrowserSessionStateArtifact } = {};
    testObjectsById: { [testObjectId: string]: HydratedAuditTarget } = {};

    // auditrequirement by specId (audit requirement itself is value object)
    requirementById: { [id: string]: AuditRequirement } = {};

    specById: { [id: string]: Spec } = {};

    hasShownFilter(filterType: FilterType): boolean {
        if (!this.shownFilterSet) {
            return true;
        }
        return this.shownFilterSet.has(filterType);
    }

    ngOnChanges() {
        this.statesByTestObjectId = this.testObjects.reduce((acc: any, obj) => {
            if (obj.target.type === 'StateObservationTarget') {
                acc[obj.testObjectId] = this.states[(obj.target as StateObservationTarget).stateId];
            }
            return acc;
        }, {});

        this.testObjectsById = this.testObjects.reduce((acc: any, obj) => {
            acc[obj.testObjectId] = obj;
            return acc;
        }, {});

        this.requirementById = (this.requirements || []).reduce((acc: any, req) => {
            acc[req.specId] = req;
            return acc;
        }, {});

        this.specById = (this.specs || []).reduce((acc: any, spec) => {
            acc[spec.id] = spec;
            return acc;
        }, {});

        const seenStatenames = new Set<string>();
        this.availableTestObjects = this.testObjects.reduce((acc, obj) => {
            if (obj.target.type === 'StateObservationTarget') {
                if (!this.statesByTestObjectId[obj.testObjectId]) {
                    return acc;
                } else {
                    if (!seenStatenames.has(this.statesByTestObjectId[obj.testObjectId].name)) {
                        seenStatenames.add(this.statesByTestObjectId[obj.testObjectId].name);
                        acc.push({
                            label: this.statesByTestObjectId[obj.testObjectId].name,
                            value: obj.testObjectId
                        });
                    }
                }

            }
            if (obj.target.type === 'TagObservationTarget') {
                acc.push({
                    label: obj.target.name,
                    value: obj.testObjectId
                })
            }
            if (obj.target.type === 'ContextObservationTarget' || obj.target.type === 'URLObservationTarget') {
                acc.push({
                    label: obj.target.name || obj.target.description || obj.testObjectId,
                    value: obj.testObjectId
                });
            }
            return acc;
        }, [] as SelectItem[]);

        const seenEvaluationStatuses = new Set<string>();
        this.availableEvaluationStatuses = this.tasks.reduce((acc, task) => {
            if (!seenEvaluationStatuses.has(task.evaluationStatus)) {
                seenEvaluationStatuses.add(task.evaluationStatus);
                acc.push({
                    label: task.evaluationStatus,
                    value: task.evaluationStatus
                });
            }
            return acc;
        }, [] as SelectItem[]);

        const seenExecutionStatuses = new Set<string>();
        this.availableExecutionStatuses = this.tasks.reduce((acc, task) => {
            if (!seenExecutionStatuses.has(task.executionStatus)) {
                seenExecutionStatuses.add(task.executionStatus);
                acc.push({
                    label: task.executionStatus,
                    value: task.executionStatus
                });
            }
            return acc;
        }, [] as SelectItem[]);

        const seenTags = new Set<string>();
        this.availableTags = this.tasks.reduce((acc, task) => {
            const req = this.requirementById[task.assessedSpec];

            const reqTags = req.labels || [];
            const specTags = this.specById[task.assessedSpec]?.labels || [];

            (reqTags.concat(specTags).concat(task.labels || [])).forEach(tag => {

                const tagStr = tagToString(tag);
                if (!seenTags.has(tagStr)) {
                    seenTags.add(tagStr);
                    acc.push({
                        label: tagToString(tag),
                        value: tag
                    });
                }
            });
            return acc;
        }, [] as SelectItem[]);

        this.shownFilterSet = this.shownFilters && new Set(this.shownFilters);
    }

    filterChanged() {
        this.computeFilter();

        if (this.filter.searchText === '' && this.filter.assessmentTaskEvaluationStatusFilterSpec === undefined && this.filter.testObjectFilterSpec === undefined &&
            this.filter.specFilterSpec === undefined && this.filter.executionStatusFilterSpec === undefined && this.filter.tagsFilterSpec === undefined && this.filter.onlyWithResults === false) {

            this.filtersActive = false;
            this.filterChange.emit(undefined);
        } else {
            this.filtersActive = true;
            this.filterChange.emit(this.filter);
        }
    }

    computeFilter() {
        this.filter = {
            searchText: this.filterText,
            assessmentTaskEvaluationStatusFilterSpec: this.selectedEvaluationStatuses.length > 0 ? this.selectedEvaluationStatuses : undefined,
            testObjectFilterSpec: this.selectedTestObjects.length > 0 ? this.selectedTestObjects : undefined,
            specFilterSpec: this.selectedSpecGroups.length > 0 ? this.selectedSpecGroups : undefined,
            executionStatusFilterSpec: this.selectedExecutionStatuses.length > 0 ? this.selectedExecutionStatuses : undefined,
            tagsFilterSpec: this.selectedTags.length > 0 ? this.selectedTags : undefined,
            onlyWithResults: this.showWithResults
        };
    }

    toggleShowFilters() {
        this.showFilters = !this.showFilters;
    }

    getLabelForSelectedTags(): string {
        return this.selectedTags.length > 0 ? this.selectedTags.map(tagToString).join(', ') : 'All Tags';
    }

    getLabelForTag(tag: string): string {
        return tag;
    }

    getLabelForSelectedSpecGroups(): string {
        return this.selectedSpecGroups.length > 0 ? this.selectedSpecGroups.join(', ') : 'All Spec Groups';
    }

    getLabelForSelectedTestObjects(): string {
        return this.selectedTestObjects.length > 0 ? this.selectedTestObjects.join(', ') : 'All Test Objects';
    }

    private getTaskTargetId(task: AssessmentTask): string | undefined {
        return task.testObjectId;
    }

    getLabelForSelectedEvaluationStatuses(): string {
        return this.selectedEvaluationStatuses.length > 0 ? this.selectedEvaluationStatuses.join(', ') : 'All Evaluation Statuses';
    }

    getLabelForSelectedExecutionStatuses(): string {
        return this.selectedExecutionStatuses.length > 0 ? this.selectedExecutionStatuses.join(', ') : 'All Execution Statuses';
    }

    protected readonly timesCircle = faTimesCircle;
    protected readonly tagToString = tagToString;
    protected readonly faFilter = faFilter;
}
